import { create } from 'zustand'
import type { AnnotationDocument } from '@/lib/annotation/schema'
import type { QueuedMarkupSave } from '@/lib/annotation/queuedMarkupSave'

/**
 * Markup the user saved in the lightbox that the server data on this device may not carry yet.
 *
 * The save's PATCH waits in the offline queue, and until it has replayed every read of the issue
 * or comment — a Mercure broadcast, a refetch, the loader's mark-read GET, a recovery
 * invalidation, the comments store — still carries the old markup. Rather than guard each of those
 * writers, the cached server data is left as it is and the pending markup is laid over it where
 * attachments are rendered (usePendingMarkupAttachments).
 *
 * A save moves through three phases, keyed by its annotationRequestUUID:
 * - `saving`: registered by the lightbox before the PATCH is queued;
 * - `queued`: the queue holds its row (or the PATCH call returned, so it was queued);
 * - `replayed`: the row is gone after having been queued — the server has it, but the parent's
 *   broadcast is published after the response (kernel.terminate) and reads already in flight
 *   still return the old markup, so the overlay holds for REPLAY_SETTLE_MS more
 *   (lib/annotation/pendingMarkupSync.ts).
 *
 * A row that has failed for good ends the save at once, and the server's markup shows again;
 * a row that will be retried, or that the licence holds, keeps it.
 */

export type PendingMarkupPhase = 'saving' | 'queued' | 'replayed'

export type PendingMarkup = {
  mediaObjectId: string
  annotationData: AnnotationDocument | null
  /** Newer saves of one image have a larger order: the queue row id once known. */
  order: number
  phase: PendingMarkupPhase
}

/** One markup save row in a queue snapshot. */
export type QueuedMarkupRow = {
  rowId: number
  save: QueuedMarkupSave
  /** False once the row has failed for good; performSync no longer replays it. */
  active: boolean
}

type PendingMarkupStore = {
  saves: Record<string, PendingMarkup>
  register: (mediaObjectId: string, annotationData: AnnotationDocument | null, uuid: string) => void
  markQueued: (uuid: string) => void
  release: (uuid: string) => void
  /** Reconcile with a full queue snapshot; returns the saves that have just replayed. */
  applyQueue: (rows: QueuedMarkupRow[]) => string[]
  reset: () => void
}

// Above any Dexie row id: a save that is not in the queue yet is newer than every row in it.
const LOCAL_ORDER_BASE = Number.MAX_SAFE_INTEGER / 2
let localOrder = 0

const usePendingMarkupStore = create<PendingMarkupStore>((set, get) => ({
  saves: {},

  register: (mediaObjectId, annotationData, uuid) =>
    set(state => ({
      saves: {
        ...state.saves,
        [uuid]: {
          mediaObjectId,
          annotationData,
          order: LOCAL_ORDER_BASE + ++localOrder,
          phase: 'saving',
        },
      },
    })),

  markQueued: uuid =>
    set(state => {
      const save = state.saves[uuid]
      if (!save || save.phase !== 'saving') return state
      return { saves: { ...state.saves, [uuid]: { ...save, phase: 'queued' } } }
    }),

  release: uuid =>
    set(state => {
      if (!(uuid in state.saves)) return state
      const saves = { ...state.saves }
      delete saves[uuid]
      return { saves }
    }),

  applyQueue: rows => {
    const byUuid = new Map(rows.map(row => [row.save.annotationRequestUUID, row]))
    const saves: Record<string, PendingMarkup> = {}
    const replayed: string[] = []
    let changed = false

    for (const [uuid, save] of Object.entries(get().saves)) {
      const row = byUuid.get(uuid)
      if (row) {
        if (!row.active) {
          changed = true
          continue
        }
        const next = { ...save, phase: 'queued' as const, order: row.rowId }
        if (next.phase !== save.phase || next.order !== save.order) changed = true
        saves[uuid] = next
      } else if (save.phase === 'queued') {
        saves[uuid] = { ...save, phase: 'replayed' }
        replayed.push(uuid)
        changed = true
      } else {
        saves[uuid] = save
      }
    }

    // Saves this session did not register: queued before a reload, or by an earlier screen.
    for (const [uuid, row] of byUuid) {
      if (uuid in saves || !row.active || uuid in get().saves) continue
      saves[uuid] = {
        mediaObjectId: row.save.mediaObjectId,
        annotationData: row.save.annotationData,
        order: row.rowId,
        phase: 'queued',
      }
      changed = true
    }

    if (changed) set({ saves })
    return replayed
  },

  reset: () => set({ saves: {} }),
}))

/** The newest pending markup per media object id. */
export function newestPendingMarkup(
  saves: Record<string, PendingMarkup>,
): Map<string, AnnotationDocument | null> {
  const newest = new Map<string, PendingMarkup>()
  for (const save of Object.values(saves)) {
    const current = newest.get(save.mediaObjectId)
    if (!current || save.order > current.order) newest.set(save.mediaObjectId, save)
  }
  return new Map([...newest].map(([id, save]) => [id, save.annotationData]))
}

export default usePendingMarkupStore
